import type { ReactNode } from 'react';
import { Card, Chip } from '@heroui/react';
import { ExternalLink, MapPin } from 'lucide-react';
import type { Location } from '@/lib/types';

interface VisitLocationCardProps {
  location: Location;
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium tracking-wide text-muted uppercase">{label}</span>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

export function VisitLocationCard({ location }: VisitLocationCardProps) {
  const hasCoordinates = location.latitude !== null && location.longitude !== null;

  return (
    <Card className="bg-surface">
      <Card.Header className="flex-row items-center justify-between">
        <Card.Title>Локация</Card.Title>
        {!location.enabled && (
          <Chip color="danger" size="sm" variant="soft">
            Неактивная локация
          </Chip>
        )}
      </Card.Header>
      <Card.Content className="flex flex-col gap-4">
        <InfoRow label="Название">
          <span className="font-medium">{location.name}</span>
        </InfoRow>
        <InfoRow label="Внутренний ID">{location.internalId}</InfoRow>
        {location.internalId2 && <InfoRow label="Внутренний ID 2">{location.internalId2}</InfoRow>}
        {location.type && <InfoRow label="Тип">{location.type}</InfoRow>}
        {location.city && <InfoRow label="Город">{location.city}</InfoRow>}
        {location.region && <InfoRow label="Округ">{location.region}</InfoRow>}
        {location.province && <InfoRow label="Область">{location.province}</InfoRow>}
        {location.district && <InfoRow label="Район">{location.district}</InfoRow>}
        {location.address && <InfoRow label="Адрес">{location.address}</InfoRow>}
        {location.phone && <InfoRow label="Телефон">{location.phone}</InfoRow>}
        {location.url && (
          <InfoRow label="Сайт">
            <a
              href={
                /^[a-z][a-z0-9+.-]*:\/\//i.test(location.url)
                  ? location.url
                  : `https://${location.url}`
              }
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 break-all text-link hover:underline"
            >
              <ExternalLink className="size-4 shrink-0" />
              {location.url}
            </a>
          </InfoRow>
        )}
        {location.description && <InfoRow label="Описание">{location.description}</InfoRow>}
        {hasCoordinates && (
          <InfoRow label="Координаты">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 text-link hover:underline"
            >
              <MapPin className="size-4 shrink-0" />
              {location.latitude}, {location.longitude}
            </a>
          </InfoRow>
        )}
      </Card.Content>
    </Card>
  );
}
